import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatNumber } from '@progress/kendo-intl';
import { downloadBlobAsFile, fetchApi, fetchByteData } from 'services/api';
import { Grid, GridCustomCellProps, GridColumn, GridCustomFooterCellProps, GridDetailRowProps, GridSortChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { ExcelExport, ExcelExportColumn as ExportColumn } from '@progress/kendo-react-excel-export';
import { ChartArea, ChartTooltip, Sparkline } from '@progress/kendo-react-charts';
import { GridLoadingPanel } from 'components/GridLoadingPanel';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { downloadIcon } from '@progress/kendo-svg-icons';
import SalesCustomerOrdersDetail from './SalesCustomerOrdersDetail';

type Customer = {
  CustomerID: number;
  CustomerNumber: string;
  CustomerName: string;
  TotalCharges: number;
  SalespersonAssignment: string;
  OrderCount: number;
  MarginPercentage: number;
  BookOfBusinessPercentage: number;
  CustomerVertical: string;
  PercentageChange: number | null;
  LastShipmentDate: string | null;
  DaysSinceLastShipment: number | null;
  OrderCountTrend: number[];
  CSVLink: string;
}

type Vertical = {
  CustomerVertical: string;
  TotalCharges: number;
  OrderCount: number;
  MarginPercentage: number;
  BookOfBusinessPercentage: number;
  OrderCountTrend: number[];
}

type SalespersonTeamSummary = {
  SalespersonID: number;
  Name: string;
  MonthToDateRevenue: number;
  YesterdayRevenue: number;
}

type Response = {
  Customers: Customer[];
  TeamSalespeople?: SalespersonTeamSummary[];
  Verticals?: Vertical[];
}

type Props = {
  salespersonId?: string;
  salespersonName: string;
  active: boolean;
}

const periodName = (period: number) => {
  switch (period) {
    case 0: return 'This Month';
    case 1: return 'This Quarter';
    case 2: return 'This Year';
    case 7: return 'Last Year';
    case 3: return 'Top By Quarter';
    case 4: return 'Unrealized';
    case 5: return 'Team';
    case 6: return 'Yesterday';
    case 8: return 'Lapsed';
    default: return '';
  }
};

const formatDate = (value: string | null) => value ? new Date(value).toLocaleDateString() : '';

const CustomerCell = (props: GridCustomCellProps, callback: (link: string) => void) => {
  if (!props.field || props.rowType !== 'data') return null;
  const dataItem = props.dataItem as Customer;
  return <td {...props.tdProps}>
    <a
      href="#"
      style={{ color: '#007bff' }}
      onClick={(e) => {
        callback(dataItem.CSVLink);
        e.preventDefault();
      }}
    >
      {dataItem.CustomerNumber}
    </a>
  </td>;
};

const TrendCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return null;
  const trend = (props.dataItem as Customer | Vertical).OrderCountTrend;
  return <td {...props.tdProps} style={{ padding: 0 }}>
    {trend && trend.length > 1
      ? <Sparkline data={trend} type="column" style={{ height: 28, width: 110 }}>
        <ChartArea background="transparent" />
        <ChartTooltip format="{0:n0} orders" />
      </Sparkline>
      : null}
  </td>;
};

const CustomersTab = ({ salespersonId, salespersonName, active }: Props) => {
  const _export = useRef<ExcelExport | null>(null);
  const [loading, setLoading] = useState(false);
  const [customerDateFilter, setCustomerDateFilter] = useState(0);
  const [useStandardCalendar, setUseStandardCalendar] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [teamSalespeople, setTeamSalespeople] = useState<SalespersonTeamSummary[]>([]);
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [verticalView, setVerticalView] = useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState('All');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  const [verticalSort, setVerticalSort] = useState<SortDescriptor[]>([]);

  // Switching filters quickly leaves several requests in flight; without this the slowest one wins and
  // its rows end up under another period's columns.
  const latestRequest = useRef(0);

  const fetchCustomers = useCallback(() => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    fetchApi(`/api/Customer/SalespersonCustomers/${salespersonId}`, { CustomerDateFilter: customerDateFilter, UseStandardCalendar: useStandardCalendar }, 'POST')
      .then((response: Response) => {
        if (requestId !== latestRequest.current) return;
        setCustomers(response.Customers ?? []);
        setTeamSalespeople(response.TeamSalespeople ?? []);
        setVerticals(response.Verticals ?? []);
      })
      .catch((e) => {
        if (requestId !== latestRequest.current) return;
        if (!e?.status) alert('Error fetching customers');
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [salespersonId, customerDateFilter, useStandardCalendar]);

  // Fetch when the tab is active (first open) and whenever the date filter changes while active.
  useEffect(() => {
    if (active) fetchCustomers();
  }, [active, fetchCustomers]);

  const download = (link: string) => {
    window.location.href = link;
  };

  const teamTotals = useMemo(() => ({
    yesterday: teamSalespeople.reduce((sum, person) => sum + person.YesterdayRevenue, 0),
    monthToDate: teamSalespeople.reduce((sum, person) => sum + person.MonthToDateRevenue, 0)
  }), [teamSalespeople]);

  const filteredCustomers = useMemo(() => {
    if (assignmentFilter === 'All') return customers;
    return customers.filter((customer) => customer.SalespersonAssignment === assignmentFilter);
  }, [assignmentFilter, customers]);

  const sortedCustomers = useMemo(() => orderBy(filteredCustomers, sort), [filteredCustomers, sort]);

  const sortedVerticals = useMemo(() => orderBy(verticals, verticalSort), [verticals, verticalSort]);

  // Team, Top By Quarter and Lapsed have no vertical roll-up behind them, so the toggle is hidden there.
  const verticalViewAvailable = customerDateFilter != 3 && customerDateFilter != 5 && customerDateFilter != 8;
  const showVerticals = verticalView && verticalViewAvailable;
  const lapsedView = customerDateFilter == 8;
  // Only the contiguous-period views carry a weekly order-count series from the server.
  const trendAvailable = customerDateFilter == 0 || customerDateFilter == 1 || customerDateFilter == 2 || customerDateFilter == 7;

  const downloadB069 = () => {
    setExportingCsv(true);
    fetchByteData(`/api/Customer/SalespersonDashboardCSV/${salespersonId}`, {
      AuthorizationCustomerIDs: filteredCustomers.map((customer) => customer.CustomerID),
      UnrealizedRevenue: customerDateFilter === 4,
      CustomerDateFilter: customerDateFilter,
      UseStandardCalendar: useStandardCalendar
    }, 'POST')
      .then((response) => downloadBlobAsFile(response.blob, response.filename))
      .catch((e) => {
        if (!e?.status) alert('Error downloading B069 report');
      })
      .finally(() => setExportingCsv(false));
  };

  const assignmentOptions = useMemo(() => ['All', ...Array.from(new Set(customers.map((customer) => customer.SalespersonAssignment))).sort()], [customers]);

  useEffect(() => {
    if (!assignmentOptions.includes(assignmentFilter)) {
      setAssignmentFilter('All');
    }
  }, [assignmentFilter, assignmentOptions]);

  const TeamSumFooterCell = (props: GridCustomFooterCellProps) => {
    if (!props.field) return <td {...props.tdProps} />;
    const value = props.field === 'YesterdayRevenue' ? teamTotals.yesterday :
      props.field === 'MonthToDateRevenue' ? teamTotals.monthToDate : 0;
    return <td {...props.tdProps}>{formatNumber(value, 'c')}</td>;
  };

  const CustomerOrdersDetail = customerDateFilter === 3
    ? undefined
    : (props: GridDetailRowProps) => (
      <SalesCustomerOrdersDetail
        {...props}
        customerDateFilter={customerDateFilter}
        salespersonId={salespersonId}
        useStandardCalendar={useStandardCalendar}
      />
    );

  const toolbarFilters = <ButtonGroup>
    <Button onClick={() => setCustomerDateFilter(2)} selected={customerDateFilter === 2} togglable>{periodName(2)}</Button>
    <Button onClick={() => setCustomerDateFilter(7)} selected={customerDateFilter === 7} togglable>{periodName(7)}</Button>
    <Button onClick={() => setCustomerDateFilter(1)} selected={customerDateFilter === 1} togglable>{periodName(1)}</Button>
    <Button onClick={() => setCustomerDateFilter(0)} selected={customerDateFilter === 0} togglable>{periodName(0)}</Button>
    <Button onClick={() => setCustomerDateFilter(6)} selected={customerDateFilter === 6} togglable>{periodName(6)}</Button>
    <Button onClick={() => setCustomerDateFilter(3)} selected={customerDateFilter === 3} togglable>{periodName(3)}</Button>
    <Button onClick={() => setCustomerDateFilter(4)} selected={customerDateFilter === 4} togglable>{periodName(4)}</Button>
    <Button onClick={() => setCustomerDateFilter(5)} selected={customerDateFilter === 5} togglable>{periodName(5)}</Button>
    <Button onClick={() => setCustomerDateFilter(8)} selected={customerDateFilter === 8} togglable title="Customers whose last shipment was 30-90 days ago">{periodName(8)}</Button>
  </ButtonGroup>;

  const viewToggle = <ButtonGroup>
    <Button selected={!verticalView} onClick={() => setVerticalView(false)} togglable>Customers</Button>
    <Button selected={verticalView} onClick={() => setVerticalView(true)} togglable>Verticals</Button>
  </ButtonGroup>;

  const assignmentFilters = <ButtonGroup>
    {assignmentOptions.map((option) => (
      <Button key={option} onClick={() => setAssignmentFilter(option)} selected={assignmentFilter === option} togglable>{option}</Button>
    ))}
  </ButtonGroup>;

  return <React.Fragment>
    {loading && <GridLoadingPanel />}
    <ExcelExport ref={_export} fileName={`${salespersonName} ${periodName(customerDateFilter)} Dashboard.xlsx`}>
      {showVerticals ? <>
        <ExportColumn field="CustomerVertical" title="Customer Vertical" />
        <ExportColumn field="TotalCharges" title="Total Charges" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="OrderCount" title="Order Count" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="MarginPercentage" title="Margin %" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="BookOfBusinessPercentage" title="% of Book" cellOptions={{ format: "0.00%" }} />
      </> : lapsedView ? <>
        <ExportColumn field="CustomerNumber" title="Customer #" />
        <ExportColumn field="CustomerName" title="Customer" />
        <ExportColumn field="SalespersonAssignment" title="Assignment" />
        <ExportColumn field="DaysSinceLastShipment" title="Days Since Last Shipment" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="LastShipmentDate" title="Last Shipment" cellOptions={{ format: "mm/dd/yyyy" }} />
        <ExportColumn field="OrderCount" title="Order Count (90d)" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="TotalCharges" title="Total Charges (90d)" cellOptions={{ format: "$#,##0.00" }} />
      </> : <>
        <ExportColumn field="CustomerNumber" title="Customer #" />
        <ExportColumn field="CustomerName" title="Customer" />
        <ExportColumn field="SalespersonAssignment" title="Assignment" />
        <ExportColumn field="TotalCharges" title="Total Charges" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="OrderCount" title="Order Count" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="MarginPercentage" title="Margin %" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="BookOfBusinessPercentage" title="% of Book" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="CustomerVertical" title="Customer Vertical" />
        {customerDateFilter == 0 && <ExportColumn field="PercentageChange" title="Last Month" cellOptions={{ format: "0%" }} />}
      </>}
    </ExcelExport>
    {customerDateFilter == 5 ? <Grid key="team" data={teamSalespeople}>
        <GridToolbar>
          {toolbarFilters}
        </GridToolbar>
        <GridColumn field="Name" title="Salesperson" />
        <GridColumn field="YesterdayRevenue" title="Yesterday Revenue" format="{0:c}" cells={{ footerCell: TeamSumFooterCell }} />
        <GridColumn field="MonthToDateRevenue" title="MTD Revenue" format="{0:c}" cells={{ footerCell: TeamSumFooterCell }} />
      </Grid>
    : showVerticals ? <Grid
      key={`verticals-${customerDateFilter}`}
      data={sortedVerticals}
      sortable
      sort={verticalSort}
      onSortChange={(e: GridSortChangeEvent) => setVerticalSort(e.sort)}
    >
      <GridToolbar>
        {toolbarFilters}
        {viewToggle}
        <ButtonGroup>
          <Button selected={!useStandardCalendar} onClick={() => setUseStandardCalendar(false)} togglable title="4-4-5 fiscal calendar">445</Button>
          <Button selected={useStandardCalendar} onClick={() => setUseStandardCalendar(true)} togglable title="Standard calendar">Standard</Button>
        </ButtonGroup>
        <Button
          svgIcon={downloadIcon}
          onClick={() => _export.current.save(sortedVerticals)}
        >Dashboard
        </Button>
      </GridToolbar>
      <GridColumn field="CustomerVertical" title="Customer Vertical" />
      <GridColumn field="TotalCharges" title="Total Charges" format="{0:c}" />
      <GridColumn field="OrderCount" title="Order Count" format="{0:n0}" />
      <GridColumn field="MarginPercentage" title="Margin %" format="{0:p2}" />
      <GridColumn field="BookOfBusinessPercentage" title="% of Book" format="{0:p2}" />
      {trendAvailable && <GridColumn title="Orders / Week" cells={{ data: TrendCell }} width={(customerDateFilter == 2 || customerDateFilter == 7) ? 194 : 98} />}
    </Grid>
    : <Grid
      key={`customers-${customerDateFilter}`}
      data={sortedCustomers}
      dataItemKey="CustomerID"
      detail={CustomerOrdersDetail}
      sortable
      sort={sort}
      onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
    >
      <GridToolbar>
        {toolbarFilters}
        {verticalViewAvailable && viewToggle}
        {assignmentFilters}
        <ButtonGroup>
          <Button selected={!useStandardCalendar} onClick={() => setUseStandardCalendar(false)} togglable title="4-4-5 fiscal calendar">445</Button>
          <Button selected={useStandardCalendar} onClick={() => setUseStandardCalendar(true)} togglable title="Standard calendar">Standard</Button>
        </ButtonGroup>
        <Button
          svgIcon={downloadIcon}
          onClick={() => _export.current.save(sortedCustomers)}
        >Dashboard
        </Button>
        <Button
          disabled={exportingCsv || filteredCustomers.length === 0}
          svgIcon={downloadIcon}
          onClick={downloadB069}
        >B069
        </Button>
      </GridToolbar>
      <GridColumn field="CustomerNumber" title="Customer #" cells={{ data: (props) => CustomerCell(props, download) }} />
      <GridColumn field="CustomerName" title="Customer" />
      <GridColumn field="SalespersonAssignment" title="Assignment" />
      {lapsedView && <GridColumn field="DaysSinceLastShipment" title="Days Since Last Shipment" />}
      {lapsedView && <GridColumn field="LastShipmentDate" title="Last Shipment" cells={{ data: (props) => <td {...props.tdProps}>{formatDate(props.dataItem.LastShipmentDate)}</td> }} />}
      <GridColumn field="TotalCharges" title={lapsedView ? "Total Charges (90d)" : "Total Charges"} format="{0:c}" />
      <GridColumn field="OrderCount" title={lapsedView ? "Order Count (90d)" : "Order Count"} />
      {!lapsedView && <GridColumn field="MarginPercentage" title="Margin %" format="{0:p2}" />}
      {!lapsedView && customerDateFilter != 3 && <GridColumn field="BookOfBusinessPercentage" title="% of Book" format="{0:p2}" />}
      {!lapsedView && <GridColumn field="CustomerVertical" title="Customer Vertical" />}
      {trendAvailable && <GridColumn title="Orders / Week" cells={{ data: TrendCell }} width={(customerDateFilter == 2 || customerDateFilter == 7) ? 194 : 98} />}
      {customerDateFilter == 0 && <GridColumn field="PercentageChange" title="Last Month" format="{0:p}" cells={{ data: (props) => <td {...props.tdProps} className={[props.tdProps?.className, props.dataItem.PercentageChange > 0 ? 'text-success' : props.dataItem.PercentageChange < 0 ? 'text-danger' : ''].filter(Boolean).join(" ")}>{props.dataItem.PercentageChange !== null ? formatNumber(props.dataItem.PercentageChange, 'p') : "N/A"}</td> }} />}
    </Grid>}
  </React.Fragment>;
};

export default CustomersTab;
